/**
 * Commissions (BE-6a/b) — real API.
 *
 * Recipients are TC users with a commission profile; rules are flat per agent (customer / service optional);
 * commissions are earned at invoice finalisation; payments are PAID (whole payable commissions), RECOVERED or
 * WRITTEN_OFF (against what the agent owes back). Backend snake_case ↔ camelCase here.
 */

import ApiService from '../api.service';
import { keysCamelToSnake, unwrapData, unwrapList, unwrapPage } from '../../utils/apiConvert';

const clean = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== undefined));
const body = (data) => keysCamelToSnake(clean(data));

export const RECIPIENT_TYPES = { EXTERNAL_AGENT: 'External Agent', EXTERNAL_PRO: 'External PRO', INTERNAL_STAFF: 'Internal Staff' };

export const commissionService = {
  // ── recipients ────────────────────────────────────────────────────────────
  getRecipients: async () => unwrapList(await ApiService.get('/api/commission-recipients')).items,
  // data: { userId, type, trnNumber } (bank details come from the person's record, read-only here)
  createRecipient: async (data) => unwrapData(await ApiService.post('/api/commission-recipients', body(data))),
  // data: { type, trnNumber, active }
  updateRecipient: async (id, data) => unwrapData(await ApiService.put(`/api/commission-recipients/${id}`, body(data))),

  // ── rates (flat rules) ────────────────────────────────────────────────────
  getRules: async () => unwrapList(await ApiService.get('/api/commission-config')).items,
  // data: { internalOrganizationId|null, internalServiceId|null, recipientId, rateType, rateValue, active? }
  createRule: async (data) => unwrapData(await ApiService.post('/api/commission-config', keysCamelToSnake(data))),
  updateRule: async (id, data) => unwrapData(await ApiService.put(`/api/commission-config/${id}`, keysCamelToSnake(data))),
  deleteRule: async (id) => ApiService.delete(`/api/commission-config/${id}`),

  // ── commissions ───────────────────────────────────────────────────────────
  // params: { recipientId, invoiceId, from, to, status: PENDING / PAYABLE / PAID, page, size }
  getCommissions: async (params) => unwrapPage(await ApiService.get('/api/commissions', { params: clean(params) })),
  getSummary: async (recipientId) => unwrapData(await ApiService.get('/api/commissions/summary', { params: { recipientId } })),

  // ── payments ──────────────────────────────────────────────────────────────
  // PAID: { recipientId, direction, commissionIds, accountId, paidAt, reference }
  // RECOVERED: { recipientId, direction, amount, accountId, paidAt, reference }
  // WRITTEN_OFF: { recipientId, direction, amount, paidAt, reference }
  recordPayment: async (data) => unwrapData(await ApiService.post('/api/commission-payments', body(data))),
  reversePayment: async (id, reason) => unwrapData(await ApiService.post(`/api/commission-payments/${id}/reverse`, { reason })),
  getPayments: async (params) => unwrapPage(await ApiService.get('/api/commission-payments', { params: clean(params) })),

  // ── the logged-in user's own ──────────────────────────────────────────────
  getMine: async (page = 0, size = 20) => unwrapPage(await ApiService.get('/api/commissions/mine', { params: { page, size } })),
  getMySummary: async () => unwrapData(await ApiService.get('/api/commissions/mine/summary')),
  getMyPayments: async (page = 0, size = 20) =>
    unwrapPage(await ApiService.get('/api/commissions/mine/payments', { params: { page, size } })),
};

export default commissionService;
